import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone, Mail } from "lucide-react";
import { BUSINESS } from "@/config";
import { CATEGORIES } from "@/data/site";
import { track } from "@/lib/analytics";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative z-10 border-t border-border bg-background" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="font-display text-4xl">Maison Neuf</p>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
              A full-service luxury salon in the heart of Hell's Kitchen — hair, nails, skin, and waxing by New York's most requested specialists.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a href={BUSINESS.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="text-muted-foreground transition-colors hover:text-foreground"><Instagram className="h-5 w-5" strokeWidth={1.5} /></a>
              <a href={BUSINESS.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="text-muted-foreground transition-colors hover:text-foreground"><Facebook className="h-5 w-5" strokeWidth={1.5} /></a>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="label mb-5">Services</p>
            <ul className="space-y-3 text-sm">
              {CATEGORIES.map((c) => (
                <li key={c.slug}><Link to={c.path} className="text-muted-foreground hover:text-foreground link-underline">{c.name}</Link></li>
              ))}
              <li><Link to="/services" className="text-muted-foreground hover:text-foreground link-underline">Full Price Menu</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="label mb-5">Salon</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-muted-foreground hover:text-foreground link-underline">About / Team</Link></li>
              <li><Link to="/gallery" className="text-muted-foreground hover:text-foreground link-underline">Gallery / Lookbook</Link></li>
              <li><Link to="/memberships" className="text-muted-foreground hover:text-foreground link-underline">Memberships</Link></li>
              <li><Link to="/reviews" className="text-muted-foreground hover:text-foreground link-underline">Reviews / Press</Link></li>
              <li><Link to="/faq" className="text-muted-foreground hover:text-foreground link-underline">Policies & FAQ</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-foreground link-underline">Contact & Location</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="label mb-5">Visit</p>
            <ul className="space-y-4 text-sm text-muted-foreground">
              <li className="flex gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-wine" strokeWidth={1.5} /><span>{BUSINESS.addressLine1}<br />{BUSINESS.addressLine2}</span></li>
              <li className="flex gap-3"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-wine" strokeWidth={1.5} /><a href={BUSINESS.phoneHref} onClick={() => track("phone_click", { location: "footer" })} className="hover:text-foreground">{BUSINESS.phoneDisplay}</a></li>
              <li className="flex gap-3"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-wine" strokeWidth={1.5} /><a href={BUSINESS.emailHref} onClick={() => track("email_click", { location: "footer" })} className="hover:text-foreground">{BUSINESS.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-border pt-8 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <p>© {year} Maison Neuf. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/terms" className="hover:text-foreground">Terms & Conditions</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
            <Link to="/accessibility" className="hover:text-foreground">Accessibility</Link>
          </div>
        </div>

        <p className="mt-8 max-w-3xl text-[0.68rem] leading-relaxed text-muted-foreground/60">
          DEMO SITE ONLY: This concept website was created by{" "}
          <a href="https://anantadigitalworks.com/" target="_blank" rel="noopener noreferrer" data-testid="agency-backlink" className="underline underline-offset-2 hover:text-foreground">Created by Ananta Digital Works</a>{" "}
          for portfolio and demonstration purposes only. Maison Neuf NY is a fictional concept; this website is not affiliated with, endorsed by, or accepting real booking requests, service appointments, or consultations for any live business.
        </p>
      </div>
    </footer>
  );
}
